import {
    isFeatureEnabled,
    toFeatureState,
} from '@etrigan/feature-toggles-client'
import { TheWestSection } from '@news-mono/common'
import {
    AppProps,
    AuthenticationHandler,
    PageResolver,
    ProvideRouteResolvedDataToTheme,
    regionalCanonicalMeta,
} from '@news-mono/component-library'
import {
    errorLocation,
    getIsFoulPlayGeoLocationBlocked,
    getRedirectRouteInfo,
    getSectionMetaInfo,
    matchedErrorRoutePages,
    Product,
    registerPageRoutes,
    RenderTargetContext,
    StaticRoutes,
} from '@news-mono/web-common'
import { isArticleLikePublication } from '@west-australian-newspapers/publication-types'
import { PageProps } from 'page-lifecycle-provider'
import queryString from 'query-string'
import React from 'react'
import { useLocation } from 'react-router'
import './App.css'
import { layout } from './App.routing'
import { staticRoutes } from './routes'
import { createAflMatchCentreRoute } from './routes/afl-match-centre-routes/get-afl-match-centre-route'
import { createCookieSettingsPage } from './routes/cookie-consent'
import { createDiagPage } from './routes/diag-routes'
import {
    matchedSomethingWentWrongRoute,
    notFoundRoute,
    staticSomethingWentWrongRoute,
} from './routes/errors'
import { getProfilePageRouteInfo } from './routes/profile/get-profile-page-route-info'
import { createArticleRoute } from './routes/publication/kind/article'
import { createFeatureRoute } from './routes/publication/kind/feature'
import { createGalleryRoute } from './routes/publication/kind/gallery'
import { getSearchPageRouteInfo } from './routes/search/get-search-page-route-info'
import { getTopicPageRouteInfo } from './routes/topic/get-topic-page-route-info'
import { Site, siteAds } from './Site'

export function initRouting() {
    registerPageRoutes<TheWestSection>({
        staticRoutes,
        matchRoutes: {
            '/newsletter-redirect': () => {
                return {
                    kind: 'redirect',
                    redirectTo: {
                        targetUrl:
                            'https://emails.thewest.com.au/subscribe/index.html',
                    },
                }
            },
            // TODO Cleanup the double function call
            '/search$': (services) =>
                getSearchPageRouteInfo(
                    queryString.parse(services.location.search),
                )(services),
            '/diag$': ({ config, resolution }) => {
                const section =
                    resolution.type === 'match' && resolution.section
                        ? resolution.section
                        : 'default'
                return createDiagPage(config, section as TheWestSection)
            },
            '/cookie-settings$': ({ resolution, store }) => {
                const section =
                    resolution.type === 'match' && resolution.section
                        ? resolution.section
                        : 'default'

                return createCookieSettingsPage(
                    store,
                    section as TheWestSection,
                )
            },
            // The following are required for regional error page matching if you want to explicitly view a regional something went wrong page
            [`${errorLocation}$`]: ({ config, getAdTargeting, resolution }) =>
                matchedSomethingWentWrongRoute(
                    config,
                    getAdTargeting,
                    resolution,
                ),
            ...(matchedErrorRoutePages as StaticRoutes<TheWestSection>),
        },
        serverRoutes: {
            profile: ({
                config,
                getAdTargeting,
                resolution,
                location,
                store,
            }) => {
                return getProfilePageRouteInfo({
                    profile: resolution.resolution.profile,
                    section: resolution.resolution.section as TheWestSection,
                    config,
                    getAdTargeting,
                    location,
                    store,
                })
            },
            redirect: ({ resolution }) => {
                return getRedirectRouteInfo(
                    resolution.resolution.redirectTo.targetUrl,
                )
            },
            topic: ({ resolution, config, getAdTargeting, store }) => {
                const { geoLocation, toggles } = store.getState()
                const geoBlockEnabled = toggles['geoblock-nsw-foul-play']
                const isGeoLocationBlocked = getIsFoulPlayGeoLocationBlocked(
                    geoLocation,
                    resolution.resolution.topic.id,
                )

                if (geoBlockEnabled && isGeoLocationBlocked) {
                    return {
                        kind: 'page',
                        heading: 'Page is Unavailable',
                        hideHeading: true,
                        pageType: 'static',
                        adTargeting: {
                            pageId: 'diag',
                            adUnitPath: '',
                            ssAdUnits: [],
                            topics: [],
                        },
                        section: 'default',
                        compositions: [
                            layout.composition({
                                type: 'box',
                                props: {},
                                contentAreas: {
                                    main: [
                                        layout.component({
                                            type: 'redirector',
                                            props: {
                                                getRedirectPathFromState: () =>
                                                    `/page-is-unavailable`,
                                                status: 302,
                                            },
                                        }),
                                    ],
                                },
                            }),
                        ],
                    }
                }
                return getTopicPageRouteInfo({
                    publicationKind: resolution.resolution.publicationKind,
                    topic: resolution.resolution.topic,
                    config,
                    section: resolution.resolution.section as TheWestSection,
                    getAdTargeting,
                    classification: resolution.resolution.classification,
                })
            },
            publication: ({
                resolution,
                config,
                store,
                getAdTargeting,
                renderTarget,
            }) => {
                const toggles = store.getState().toggles
                const regionalMeta = isFeatureEnabled(
                    toFeatureState(toggles),
                    'regionals',
                    false,
                )
                    ? regionalCanonicalMeta(
                          config,
                          store.getState().meta.sectionMeta,
                          resolution.resolution.publication,
                      )
                    : undefined
                const sectionMeta = getSectionMetaInfo(
                    store.getState().meta.sectionMeta,
                    resolution.resolution.section as TheWestSection,
                )
                if (
                    resolution.resolution.publication.kind === 'redirect' &&
                    resolution.resolution.publication.redirectUrl
                ) {
                    return getRedirectRouteInfo(
                        resolution.resolution.publication.redirectUrl,
                    )
                }
                if (
                    isArticleLikePublication(resolution.resolution.publication)
                ) {
                    const shouldResolveFeatureRoute = isFeatureEnabled(
                        toFeatureState(toggles),
                        'immersive-story-article-template',
                    )

                    const { geoLocation } = store.getState()
                    const geoBlockEnabled = toggles['geoblock-nsw-foul-play']

                    const isGeoLocationBlocked =
                        getIsFoulPlayGeoLocationBlocked(
                            geoLocation,
                            resolution.resolution.publication.topics.primary.id,
                            resolution.resolution.publication.topics.secondary,
                        )

                    if (isGeoLocationBlocked && geoBlockEnabled) {
                        return {
                            kind: 'page',
                            heading: 'Page is Unavailable',
                            hideHeading: true,
                            pageType: 'static',
                            adTargeting: {
                                pageId: 'diag',
                                adUnitPath: '',
                                ssAdUnits: [],
                                topics: [],
                            },
                            section: 'default',
                            compositions: [
                                layout.composition({
                                    type: 'box',
                                    props: {},
                                    contentAreas: {
                                        main: [
                                            layout.component({
                                                type: 'redirector',
                                                props: {
                                                    getRedirectPathFromState:
                                                        () =>
                                                            `/page-is-unavailable`,
                                                    status: 302,
                                                },
                                            }),
                                        ],
                                    },
                                }),
                            ],
                        }
                    }

                    if (
                        resolution.resolution.publication.layoutView ===
                            'feature' &&
                        shouldResolveFeatureRoute
                    ) {
                        return createFeatureRoute({
                            config,
                            article: resolution.resolution.publication,
                            section: resolution.resolution
                                .section as TheWestSection,
                            getAdTargeting,
                            sectionMeta,
                            pageMeta: regionalMeta,
                        })
                    }

                    return createArticleRoute({
                        config,
                        article: resolution.resolution.publication,
                        section: resolution.resolution
                            .section as TheWestSection,
                        getAdTargeting,
                        pageMeta: regionalMeta,
                        renderTarget,
                        toggles,
                    })
                }
                if (resolution.resolution.publication.kind === 'gallery') {
                    return createGalleryRoute({
                        gallery: resolution.resolution.publication,
                        numberGalleryItems:
                            resolution.resolution.publication.imageCount,
                        section: resolution.resolution
                            .section as TheWestSection,
                        getAdTargeting,
                        pageMeta: regionalMeta,
                    })
                }
                return null
            },
            preview: ({ resolution, config, getAdTargeting, renderTarget }) => {
                if (
                    isArticleLikePublication(resolution.resolution.publication)
                ) {
                    return createArticleRoute({
                        config,
                        article: resolution.resolution.publication,
                        section: resolution.resolution
                            .section as TheWestSection,
                        getAdTargeting,
                        renderTarget,
                    })
                }
                if (resolution.resolution.publication.kind === 'gallery') {
                    return createGalleryRoute({
                        gallery: resolution.resolution.publication,
                        numberGalleryItems:
                            resolution.resolution.publication.imageCount,
                        section: resolution.resolution
                            .section as TheWestSection,
                        getAdTargeting,
                    })
                }

                return null
            },
            'video-series': () => {
                // TODO DPT-1990 implement video series landing page
                return null
            },
            'video-series-episode': () => {
                // TODO DPT-1991 implement video series episode page
                return null
            },
            video: () => {
                return null
            },
            'video-topic': () => {
                return null
            },
            'match-centre': () => {
                return null
            },
            'afl-match-centre': ({
                config,
                getAdTargeting,
                resolution,
                location,
                store,
            }) => {
                const { toggles } = store.getState()
                const isMatchCentreEnabled =
                    toggles['the-west-afl-fixture-scoreboard']
                if (!isMatchCentreEnabled) {
                    return null
                }
                return createAflMatchCentreRoute({
                    config,
                    getAdTargeting,
                    location,
                    meta: resolution.resolution.meta,
                    match: resolution.resolution.match,
                    competition: resolution.resolution.competition,
                    season: resolution.resolution.season,
                    apiData: resolution.resolution.apiData,
                })
            },
            'fifa-wwc-match-centre': () => {
                return null
            },
        },
        errorRouteInfo: staticSomethingWentWrongRoute,
        notFoundRouteInfo: notFoundRoute,
    })
}

initRouting()
export const App: React.FC<AppProps> = ({
    onEvent,
    hostname,
    protocol,
    services,
    gptApi,
}) => {
    const location = useLocation()
    const { renderTarget } = React.useContext(RenderTargetContext)
    return (
        <PageResolver
            stretchColumns={false}
            layout={layout}
            hostname={hostname}
            protocol={protocol}
            location={location}
            onEvent={onEvent}
            siteAds={siteAds}
            services={services}
            renderTarget={renderTarget}
            gptApi={gptApi}
            renderPage={(
                pageContents,
                ads,
                pageType,
                section,
                additionalPageProperties,
            ) => {
                const isCountrymanInApp =
                    renderTarget === 'app' && section === 'countryman'
                const resolvedSection = isCountrymanInApp
                    ? 'default'
                    : (section as TheWestSection)
                return (
                    <ProvideRouteResolvedDataToTheme
                        product={Product.TheWest}
                        section={resolvedSection}
                    >
                        {/**
                         * These page properties, are used to represent the state of the
                         * design of the current iteration of the website, and should stay for data integrity
                         * until such a point in time arises where the margins or headline style is altered
                         */}
                        <PageProps
                            pageProperties={{
                                marginSize: 'smaller-margins',
                                headlineStyle: 'serif-headlines',
                            }}
                        />
                        <AuthenticationHandler
                            log={services.log}
                            config={services.config}
                            onEvent={onEvent}
                        />
                        <Site
                            pageType={pageType}
                            section={resolvedSection}
                            additionalPageProperties={additionalPageProperties}
                            config={services.config}
                            onEvent={onEvent}
                            pageContents={pageContents}
                            location={location}
                            ads={ads}
                            hostname={hostname || ''}
                        />
                    </ProvideRouteResolvedDataToTheme>
                )
            }}
        />
    )
}
